import React, { useEffect, useState } from 'react';
import { FaPlus, FaTrash, FaTimes } from 'react-icons/fa';
import { api } from '../../utils/api';
import ImageUploader from '../../components/ImageUploader';
import LoadingSpinner from '../../components/LoadingSpinner';
import {
  PageHeader, Panel, FormGroup, SaveBtn, SecondaryBtn, DangerBtn,
  Toast, Row, SectionCard, ItemPill, ImageStrip, ImageThumb,
} from './styles/AdminStyles';

const emptySection = () => ({ key: '', title: '', description: '', items: [], images: [] });

const EditAbout = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    api.get('/about').then(d => {
      setData({ ...d, sections: Array.isArray(d.sections) ? d.sections : [] });
      setLoading(false);
    });
  }, []);

  if (loading || !data) return <LoadingSpinner fullPage />;

  const setField = (f) => (e) => setData({ ...data, [f]: e.target.value });
  const setBanner = (val) => setData({ ...data, bannerImage: val });

  const updateSection = (i, patch) => {
    const next = [...data.sections];
    next[i] = { ...next[i], ...patch };
    setData({ ...data, sections: next });
  };

  const addSection = () => setData({ ...data, sections: [...data.sections, emptySection()] });
  const removeSection = (i) => {
    const next = data.sections.filter((_, idx) => idx !== i);
    setData({ ...data, sections: next });
  };

  const addItem = (i, value) => {
    if (!value) return;
    const section = data.sections[i];
    updateSection(i, { items: [...(section.items || []), value] });
  };
  const removeItem = (i, j) => {
    const section = data.sections[i];
    updateSection(i, { items: section.items.filter((_, idx) => idx !== j) });
  };

  const addImage = (i, img) => {
    const section = data.sections[i];
    updateSection(i, { images: [...(section.images || []), img] });
  };
  const removeImage = (i, j) => {
    const section = data.sections[i];
    updateSection(i, { images: section.images.filter((_, idx) => idx !== j) });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setToast(null);
    try {
      const payload = {
        title: data.title || '',
        description: data.description || '',
        bannerImage: data.bannerImage || '',
        sections: data.sections.map(s => ({
          key: s.key,
          title: s.title || '',
          description: s.description || '',
          items: Array.isArray(s.items) ? s.items : [],
          images: Array.isArray(s.images) ? s.images : [],
        })),
      };
      const saved = await api.put('/about', payload);
      setData({ ...saved, sections: Array.isArray(saved.sections) ? saved.sections : [] });
      api.clearCache();
      setToast({ type: 'success', message: 'About page saved.' });
    } catch (err) {
      setToast({ type: 'error', message: err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save}>
      <PageHeader>
        <div className="eyebrow">Content</div>
        <h1>Edit About Page</h1>
        <p>Manage the About page banner, intro and sections. Sections render in order on the public site.</p>
      </PageHeader>

      <Panel>
        <h2>Page Header</h2>
        <div className="subtitle">Banner image and introduction text.</div>
        <ImageUploader
          label="Banner Image"
          value={data.bannerImage}
          onChange={setBanner}
          onRemove={() => setBanner('')}
        />
        <FormGroup>
          <label>Title</label>
          <input value={data.title || ''} onChange={setField('title')} />
        </FormGroup>
        <FormGroup>
          <label>Description</label>
          <textarea value={data.description || ''} onChange={setField('description')} />
        </FormGroup>
      </Panel>

      <Panel>
        <h2>Sections</h2>
        <div className="subtitle">Each section renders as its own block on the About page.</div>

        {data.sections.map((s, i) => (
          <SectionCard key={i}>
            <h3>
              Section {i + 1}{s.key ? ` — ${s.key}` : ''}
              <DangerBtn type="button" onClick={() => removeSection(i)}>
                <FaTrash /> Remove
              </DangerBtn>
            </h3>
            <FormGroup>
              <label>Key (unique identifier, e.g. mission, vision)</label>
              <input value={s.key || ''} onChange={e => updateSection(i, { key: e.target.value })} />
            </FormGroup>
            <FormGroup>
              <label>Title</label>
              <input value={s.title || ''} onChange={e => updateSection(i, { title: e.target.value })} />
            </FormGroup>
            <FormGroup>
              <label>Description</label>
              <textarea value={s.description || ''} onChange={e => updateSection(i, { description: e.target.value })} />
            </FormGroup>

            <FormGroup>
              <label>Items (list)</label>
              <ItemInput onAdd={(val) => addItem(i, val)} />
              <div>
                {(s.items || []).map((it, j) => (
                  <ItemPill key={j}>
                    {it}
                    <button type="button" onClick={() => removeItem(i, j)}><FaTimes /></button>
                  </ItemPill>
                ))}
              </div>
            </FormGroup>

            <FormGroup>
              <label>Images</label>
              <ImageUploader
                label=""
                value={null}
                onChange={(img) => addImage(i, img)}
              />
              <ImageStrip>
                {(s.images || []).map((img, j) => (
                  <ImageThumb key={j}>
                    <img src={img} alt="" />
                    <button type="button" onClick={() => removeImage(i, j)}><FaTimes /></button>
                  </ImageThumb>
                ))}
              </ImageStrip>
            </FormGroup>
          </SectionCard>
        ))}

        <SecondaryBtn type="button" onClick={addSection}><FaPlus /> Add Section</SecondaryBtn>
      </Panel>

      <Row>
        <SaveBtn type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</SaveBtn>
        {toast && <Toast $error={toast.type === 'error'}>{toast.message}</Toast>}
      </Row>
    </form>
  );
};

const ItemInput = ({ onAdd }) => {
  const [v, setV] = useState('');
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
      <input
        value={v}
        onChange={e => setV(e.target.value)}
        placeholder="Add an item and press Enter"
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (v.trim()) { onAdd(v.trim()); setV(''); }
          }
        }}
      />
      <SecondaryBtn type="button" onClick={() => { if (v.trim()) { onAdd(v.trim()); setV(''); } }}>Add</SecondaryBtn>
    </div>
  );
};

export default EditAbout;
